import classNames from 'classnames';
import { type FC, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'wouter';

import { Button, ButtonSize, ButtonStyle } from '@atlas/components';
import { setSelfExclusionSettingsRequest } from 'api/responsibleGaming';
import Collapsible from 'components/Collapsible/Collapsible';
import Icon from 'components/Icon/Icon';
import { useConfig } from 'config';
import { useClientProfile } from 'store';
import { AUTH_PAGES } from 'utils/authRedirects';
import { Translation } from 'utils/translations';
import useLazyRequest from 'utils/useLazyRequest';

import styles from './styles.module.scss';

type Period = {
  timeRange: number;
  text: string;
};

const SelfExclusionsSection: FC = () => {
  const [{ sessionToken }, { logout }] = useClientProfile();
  const [selectedPeriod, setSelectedPeriod] = useState<Period | null>();
  const [, setLocation] = useLocation();

  const { execute } = useLazyRequest(setSelfExclusionSettingsRequest);

  const configPeriods = useConfig(
    'config.siteConfig.responsibleGaming.selfExclusionPeriods',
  );
  const periods = useMemo(() => {
    const enabledPeriods = configPeriods
      .filter(
        (period): period is { enabled: true; timeRange: number } =>
          'timeRange' in period && period.enabled,
      )
      .sort((a, b) => a.timeRange - b.timeRange)
      .map(({ timeRange }) => ({
        text: `${timeRange}_day${timeRange > 1 ? 's' : ''}`,
        timeRange,
      }));

    const hasForever = configPeriods.some(
      (period) => 'forever' in period && period.enabled,
    );

    if (hasForever) {
      enabledPeriods.push({
        text: 'forever',
        timeRange: 0,
      });
    }

    return enabledPeriods;
  }, [configPeriods]);

  const handleClick = useCallback(
    (period: Period) => {
      if (period.timeRange !== selectedPeriod?.timeRange) {
        setSelectedPeriod(period);
      } else {
        setSelectedPeriod(null);
      }
    },
    [selectedPeriod?.timeRange],
  );

  const handleSave = useCallback(() => {
    if (selectedPeriod?.timeRange) {
      execute({
        headers: {
          authorization: `Bearer ${sessionToken}`,
        },
        body: {
          days: selectedPeriod.timeRange,
        },
      });
      logout();
      setLocation(`~${AUTH_PAGES.signIn}`);
    }
  }, [selectedPeriod, execute, sessionToken, logout, setLocation]);

  return (
    <Collapsible className={styles.control}>
      <Collapsible.Trigger>
        {({ expanded }) => (
          <div className={styles['control-header']}>
            <Icon
              name="clipboard-close.bold"
              width={48}
              height={48}
              className={styles.icon}
            />
            <div className={styles['control-header-content']}>
              <div className={styles.title}>
                <Translation message="about_self_exclusion" />
              </div>
              <p className={styles.description}>
                <Translation message="self_exclusion_message" />
              </p>
            </div>
            <Icon
              name="arrow-right.circle"
              className={classNames(styles.arrow, {
                [styles['arrow-expanded']]: expanded,
              })}
            />
          </div>
        )}
      </Collapsible.Trigger>
      <Collapsible.Content>
        {({ toggle }) => (
          <>
            <div className={classNames(styles.buttons)}>
              {periods.map((period) => (
                <Button
                  size={ButtonSize.small}
                  style={ButtonStyle.outline}
                  key={period.timeRange}
                  onClick={() => handleClick(period)}
                  className={classNames(styles['buttons-item'], {
                    [styles.active]:
                      selectedPeriod?.timeRange === period.timeRange,
                  })}
                >
                  <Translation message={period.text} />
                </Button>
              ))}
            </div>

            <div className={styles['action-buttons']}>
              <Button
                onClick={() => {
                  handleSave();
                  toggle();
                }}
              >
                <Translation message="save" />
              </Button>
              <Button
                onClick={() => {
                  toggle();
                  setSelectedPeriod(null);
                }}
                style={ButtonStyle.outline}
              >
                <Translation message="cancel" />
              </Button>
            </div>
          </>
        )}
      </Collapsible.Content>
    </Collapsible>
  );
};

export default SelfExclusionsSection;
